import React from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaBriefcase,
  FaCalendarAlt,
  FaHeadset,
  FaHome,
  FaSearch,
  FaTicketAlt,
  FaTrain,
  FaUsers,
} from "react-icons/fa";
import RailSearchWidget from "../helpers/RailSearchWidget";

const iconMap = {
  booking: FaTicketAlt,
  deals: FaSearch,
  schedules: FaCalendarAlt,
  support: FaHeadset,
  family: FaUsers,
  business: FaBriefcase,
};

export default function RailServicePage({
  eyebrow,
  title,
  description,
  icon = "booking",
  highlights = [],
  steps = [],
}) {
  const Icon = iconMap[icon] || FaTrain;

  return (
    <div className="bg-[#f8f9fc] font-sans">
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(40,113,211,0.45),transparent_34%),linear-gradient(135deg,#0f172a,#1d4ed8)]" />
        <div className="relative mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
          <nav className="mb-6 flex flex-wrap items-center gap-2 text-sm font-bold text-blue-100">
            <Link to="/" className="transition hover:text-white">
              Home
            </Link>
            <FaArrowRight className="h-3 w-3" />
            <Link to="/rail" className="transition hover:text-white">
              Rail
            </Link>
            <FaArrowRight className="h-3 w-3" />
            <span className="text-white">{eyebrow}</span>
          </nav>

          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_280px] lg:items-end">
            <div>
              <p className="mb-4 inline-flex rounded-full bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-blue-100 ring-1 ring-white/15">
                {eyebrow}
              </p>
              <h1 className="max-w-4xl text-4xl font-black leading-tight tracking-tight sm:text-6xl">
                {title}
              </h1>
              <p className="mt-5 max-w-3xl text-base font-medium leading-8 text-blue-50 sm:text-lg">
                {description}
              </p>
            </div>
            <div className="rounded-[1.5rem] border border-white/15 bg-white/10 p-6 backdrop-blur-xl">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-[#2871d3]">
                <Icon className="h-6 w-6" />
              </div>
              <div className="mt-5 text-sm font-bold uppercase tracking-[0.18em] text-blue-100">
                Rail support
              </div>
              <div className="mt-2 text-2xl font-black">1-888-338-5981</div>
            </div>
          </div>
        </div>
      </section>

      {/* <RailSearchWidget /> */}

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <main className="space-y-6">
            <article className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-[0_16px_45px_rgba(15,23,42,0.06)] sm:p-8">
              <h2 className="text-3xl font-black tracking-tight text-slate-950">
                {title}
              </h2>
              <p className="mt-4 text-base font-medium leading-8 text-slate-600">
                Use CheapOflyHub to compare rail routes, travel dates, passenger
                needs, and booking options. Our advisors can help with
                schedules, ticket rules, changes, cancellations, and trip
                planning.
              </p>
            </article>

            <div className="grid gap-5 sm:grid-cols-2">
              {highlights.map((item) => (
                <div
                  key={item.title}
                  className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-sm"
                >
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-[#2871d3]">
                    <FaTrain />
                  </div>
                  <h3 className="text-xl font-black text-slate-950">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm font-medium leading-7 text-slate-600">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>

            <section className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <h2 className="text-2xl font-black tracking-tight text-slate-950">
                How it works
              </h2>
              <div className="mt-6 grid gap-4">
                {steps.map((step, index) => (
                  <div
                    key={step}
                    className="flex gap-4 rounded-2xl bg-[#f8f9fc] p-4"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#2871d3] text-sm font-black text-white">
                      {index + 1}
                    </span>
                    <p className="text-sm font-semibold leading-6 text-slate-600">
                      {step}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          </main>

          <aside className="space-y-5">
            <div className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-[0_16px_45px_rgba(15,23,42,0.06)]">
              <h2 className="text-lg font-black text-slate-950">
                Ready to plan?
              </h2>
              <p className="mt-2 text-sm font-medium leading-6 text-slate-500">
                Search rail options above or call for live booking help.
              </p>
              <a
                href="tel:+18883385981"
                className="mt-5 flex w-full items-center justify-center rounded-full bg-[#2871d3] px-5 py-3 text-sm font-black text-white shadow-lg shadow-blue-600/20 transition hover:bg-[#155bc2]"
              >
                Call 1-888-338-5981
              </a>
            </div>

            <div className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <FaHome className="text-[#2871d3]" />
                <span className="text-sm font-black text-slate-950">
                  Rail fares and rules vary by route, date, and provider.
                </span>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
